<template>
  <div>
    <div
      class="permission-row d-flex align-items-start"
      :class="{
        'permission-row--selected': selected === node.name,
        'permission-row--locked': inherited,
      }"
      :style="{ 'margin-left': `${depth * 18}px` }"
      @click="handleClick"
    >
      <div
        class="expander"
        @click.stop="expanded = !expanded"
      >
        <i
          v-if="node.children && node.children.length"
          class="fas fa-fw"
          :class="isExpanded ? 'fa-chevron-down' : 'fa-chevron-right'"
        />
      </div>

      <div class="indicator mr-2 mt-1">
        <CustomCheckbox
          :value="effectiveAssigned"
          :indeterminate="!effectiveAssigned && assignedDescendantCount > 0"
          :clickable="false"
          color="hub"
        />
      </div>

      <div class="flex-grow-1 min-w-0">
        <div class="d-flex align-items-center">
          <span class="permission-name">{{ node.text }}</span>
          <span
            v-if="node.children && node.children.length"
            v-tippy
            class="badge badge-pill badge-light count-badge ml-2"
            :content="assignedTitlesTooltip"
          >
            {{ assignedDescendantCount }} {{ $t('general.of') }} {{ totalDescendantCount }}
          </span>
          <span
            v-if="inherited"
            v-tippy
            class="badge badge-pill inherited-badge ml-2"
            :content="$t('permissions.inheritedTooltip')"
          >
            <i class="fas fa-lock mr-1" />{{ $t('permissions.includedLabel') }}
          </span>
        </div>
        <div
          v-if="node.description"
          class="permission-description"
        >
          {{ node.description }}
        </div>
        <div
          v-if="effectiveTargets.length"
          class="configured-targets"
        >
          <span
            v-for="target in effectiveTargets"
            :key="target"
            v-tippy
            class="configured-target-pill"
            :content="target"
          >
            {{ target }}
          </span>
        </div>
      </div>
    </div>

    <div v-if="isExpanded && visibleChildren.length">
      <PermissionRow
        v-for="child in visibleChildren"
        :key="child.name"
        :node="child"
        :assigned-permissions="assignedPermissions"
        :selected="selected"
        :search="search"
        :inherited="effectiveAssigned"
        :inherited-targets="effectiveTargets"
        :depth="depth + 1"
        @select="$emit('select', $event)"
      />
    </div>
  </div>
</template>

<script>
import { mapState } from 'vuex';
import CustomCheckbox from '@core/components/CustomCheckbox';
import permissionMatchesQuery from '@/utils/permissionsSearch';

export default {
  name: 'PermissionRow',
  props: {
    node: {
      type: Object,
      required: true,
    },
    assignedPermissions: {
      type: Array,
      default: () => [],
    },
    selected: String,
    search: {
      type: String,
      default: '',
    },
    inherited: {
      type: Boolean,
      default: false,
    },
    inheritedTargets: {
      type: Array,
      default: () => [],
    },
    depth: {
      type: Number,
      default: 0,
    },
  },
  data() {
    return {
      expanded: this.depth === 0,
    };
  },
  components: {
    CustomCheckbox,
  },
  computed: {
    ...mapState({
      plants: state => state.structure.plants || [],
      elements: state => state.structure.elements || [],
      tenants: state => state.tenant.tenants || [],
      allRoles: state => state.role.allRoles || [],
      plantsLp: state => state.plantsLp.plantsLp || [],
    }),
    isAssigned() {
      return this.assignedPermissions.some(p => p.name === this.node.name);
    },
    ownAssignedPermissions() {
      return this.assignedPermissions.filter(p => p.name === this.node.name);
    },
    hasMultiplePlants() {
      const plantIds = this.ownAssignedPermissions
        .map(p => this.elements.find(x => x.id === p.value)?.plantId)
        .filter(Boolean);
      return new Set(plantIds).size > 1;
    },
    ownConfiguredTargets() {
      const arg = (this.node.arg || '').toLowerCase();
      if (!arg || arg === 'none') return [];

      return this.ownAssignedPermissions
        .map(p => this.resolveTargetName(arg, p.value))
        .filter(Boolean)
        .sort((a, b) => a.localeCompare(b, undefined, { numeric: true, sensitivity: 'base' }));
    },
    effectiveTargets() {
      return this.isAssigned ? this.ownConfiguredTargets : this.inheritedTargets;
    },
    effectiveAssigned() {
      return this.isAssigned || this.inherited;
    },
    assignedDescendantCount() {
      return this.countMatching(this.node, n =>
        this.assignedPermissions.some(p => p.name === n.name), this.inherited);
    },
    totalDescendantCount() {
      return this.countMatching(this.node, () => true);
    },
    assignedTitles() {
      return this.collectAssignedTitles(this.node, this.inherited);
    },
    assignedTitlesTooltip() {
      if (!this.assignedTitles.length) return '';
      return `<ul class="permission-tooltip-list">${
        this.assignedTitles.map(title => `<li>${this.escapeHtml(title)}</li>`).join('')
      }</ul>`;
    },
    isExpanded() {
      return !!this.search || this.expanded;
    },
    visibleChildren() {
      return (this.node.children || [])
        .filter(child => permissionMatchesQuery(child, this.search));
    },
  },
  methods: {
    resolveTargetName(arg, value) {
      if (arg === 'elementid' || arg === 'lineid') {
        const element = this.elements.find(x => x.id === value);
        if (!element) return undefined;
        const plantName = this.hasMultiplePlants
          ? this.plants.find(p => p.id === element.plantId)?.name
          : undefined;
        return plantName ? `${plantName} / ${element.name}` : element.name;
      }
      if (arg === 'tenantid') {
        return this.tenants.find(x => x.id === value)?.name;
      }
      if (arg === 'groupid') {
        return this.allRoles.find(x => x.id === value)?.name;
      }
      if (arg === 'lpplantid') {
        return this.plantsLp.find(x => x.id === value)?.name;
      }
      return this.plants.find(x => x.id === value)?.name;
    },
    handleClick() {
      if (this.inherited) return;
      this.$emit('select', this.node.name);
    },
    countMatching(node, predicate, inherited = false) {
      const effectivelyMatched = inherited || predicate(node);
      const self = effectivelyMatched ? 1 : 0;
      const children = (node.children || [])
        .reduce((sum, child) => sum + this.countMatching(child, predicate, effectivelyMatched), 0);
      return self + children;
    },
    collectAssignedTitles(node, inherited = false) {
      const isNodeAssigned = inherited
        || this.assignedPermissions.some(p => p.name === node.name);
      const titles = isNodeAssigned ? [node.text] : [];
      const childTitles = (node.children || [])
        .flatMap(child => this.collectAssignedTitles(child, isNodeAssigned));
      return titles.concat(childTitles);
    },
    escapeHtml(text) {
      return text
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;');
    },
  },
};
</script>

<style lang="scss" scoped>
.permission-row {
  padding: 6px 8px;
  border-radius: 6px;
  cursor: pointer;
  border: 1px solid transparent;
  transition: 0.2s all;
  margin-bottom: 0.25rem;

  &:hover {
    background-color: #fefefe;;
  }

  &--selected {
    background-color: #fff;
    border-color: var(--brand-color, #c4c3c3);
  }

  &--locked {
    cursor: not-allowed;
    opacity: 0.6;

    &:hover {
      background-color: transparent;
    }
  }
}

.inherited-badge {
  background-color: #303132;
  color: #fff;
  font-size: 10px;
  font-weight: 600;
  letter-spacing: 0.02em;
  text-transform: uppercase;
  display: inline-flex;
  align-items: center;
}

.expander {
  width: 30px;
  text-align: left;
  cursor: pointer;
}

.min-w-0 {
  min-width: 0;
}

.permission-name {
  font-weight: 500;
  font-size: 0.9rem;
}

.permission-description {
  font-size: 0.78rem;
  color: #868e96;
  line-height: 1.2;
  margin-top: 1px;
}

.configured-targets {
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
  margin-top: 6px;
}

.configured-target-pill {
  display: inline-block;
  max-width: 120px;
  padding: 2px 7px;
  border-radius: 20px;
  background-color: #646668;
  color: #fff;
  opacity: 0.7;
  font-size: 9px;
  font-weight: 500;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  vertical-align: top;
}

.count-badge {
  color: white;
  background-color: #646668;
  font-size: 10px;
}
</style>

<style lang="scss">
.permission-tooltip-list {
  margin: 0;
  padding-left: 16px;
  text-align: left;

  li {
    white-space: nowrap;
  }
}
</style>
